<template>
  <div class="pb-home-content">
    <DataViewHeader>
      <slot />
    </DataViewHeader>

    <UiTable
      v-if="requests.length || loading"
      id="requests-list"
      :is-loading="loading"
      :loading-row-count="placeholderCount"
    >
      <template #header>
        <DataViewSortHeader
          field="SRQ_SSL_NAME"
          class="pb-column-header"
          v-bind="sortProps.header"
        >
          {{ translate('generic.name') }}
        </DataViewSortHeader>
        <DataViewSortHeader
          field="SRQ_SSL_BRAND"
          class="pb-column-header pb-requests-list-column-brand"
          v-bind="sortProps.header"
        >
          {{ translate('generic.brand') }}
        </DataViewSortHeader>
        <DataViewSortHeader
          field="SRQ_STATE"
          class="pb-column-header"
          v-bind="sortProps.header"
        >
          {{ translate('generic.status') }}
        </DataViewSortHeader>
        <DataViewSortHeader
          field="SRQ_DATE_REQUEST"
          class="pb-column-header"
          v-bind="sortProps.header"
        >
          {{ translate('generic.date') }}
        </DataViewSortHeader>
        <TableCell
          class="pb-column-header"
          head
          center
        >
          {{ translate('pb2.requestsList.documentationColumn.header') }}
        </TableCell>
        <TableCell
          class="pb-column-header"
          head
          center
        >
          {{ translate('pb2.requestsList.ticketColumn.header') }}
        </TableCell>
        <TableCell
          class="pb-column-header"
          head
          center
        >
          {{ translate('pb2.requestsList.orderColumn.header') }}
        </TableCell>
        <TableCell
          class="pb-column-header"
          head
          center
        >
          {{ translate('pb2.requestsList.profileColumn.header') }}
        </TableCell>
        <TableCell
          class="pb-column-header"
          head
          center
        >
          {{ translate('pb2.requestsList.downloadColumn.header') }}
        </TableCell>
      </template>

      <TableRow
        v-for="request in requests"
        :key="request.SRQ_ID"
      >
        <TableCell
          left
          strong
        >
          {{ request.SRQ_SSL_NAME }}
        </TableCell>
        <TableCell
          left
          muted
        >
          {{ request.SRQ_SSL_BRAND }}
        </TableCell>
        <TableCell
          muted
        >
          <UiIcon
            icon="dot"
            :color="request.statusStyle"
          />
          {{ request.formattedStatus }}
        </TableCell>
        <TableCell
          muted
        >
          {{ request.formattedRequestDate }}
        </TableCell>
        <TableCell
          class="pb-requests-list-column-icons pb-icon-cell"
          center
          :link="{ href: request.documentationLink }"
        >
          <UiIcon
            v-tooltip="{ text: translate('pb2.requestsList.documentationColumn.iconTooltip') }"
            icon="paperclip"
            class="pb-icon-cell-item"
          />
        </TableCell>
        <TableCell
          class="pb-requests-list-column-icons pb-icon-cell"
          center
          :link="{ href: request.ticketLink }"
        >
          <UiIcon
            v-tooltip="{ text: translate('pb2.requestsList.ticketColumn.iconTooltip') }"
            icon="logs"
            class="pb-icon-cell-item"
          />
        </TableCell>
        <TableCell
          class="pb-requests-list-column-icons pb-icon-cell"
          center
          :link="request.orderLink ? { href: request.orderLink } : null"
        >
          <template v-if="request.orderLink">
            <!-- <UiIcon
              v-tooltip="{ text: translate('pb2.requestsList.orderColumn.iconTooltip') }"
              icon="storeCart"
              class="pb-icon-cell-item"
            /> -->
            <UiBadge
              v-tooltip="{ text: translate('pb2.requestsList.orderColumn.iconTooltip') }"
              :color="request.orderPaid ? 'success' : 'error'"
              interactive
            >
              {{ translate(request.orderPaid ? 'pb2.requestsList.orderColumn.paid' : 'pb2.requestsList.orderColumn.unpaid') }}
            </UiBadge>
          </template>
        </TableCell>
        <TableCell
          class="pb-requests-list-column-icons pb-icon-cell"
          center
          :to="request.profileLink"
        >
          <UiIcon
            v-if="request.profileLink"
            v-tooltip="{ text: translate('pb2.requestsList.profileColumn.iconTooltip') }"
            icon="show"
            class="pb-icon-cell-item"
          />
        </TableCell>
        <TableCell
          class="pb-requests-list-column-icons pb-icon-cell"
          center
          :to="request.profileDownloadLink"
        >
          <UiIcon
            v-if="request.profileDownloadLink"
            v-tooltip="{ text: translate('pb2.requestsList.downloadColumn.iconTooltip') }"
            icon="download"
            class="pb-icon-cell-item"
          />
        </TableCell>
      </TableRow>
    </UiTable>
    <NoItemFound v-else />

    <DataViewFooter
      pagination
      goto
      v-bind="paginationProps.footer"
    >
      <SelectField
        class="pb-dataview-limit"
        :aria-label="translate('pb2.homeView.limitLabel')"
        :size="SIZE_SMALL"
        v-bind="paginationProps.limit"
      />
      <UiTypo>
        {{ paginationText }}
      </UiTypo>
    </DataViewFooter>
  </div>
</template>

<script>
import { computed, markRaw, watch } from 'vue'

import routeNames from '@/router/names'
import * as sdk from '@/helpers/sdk'
import buildDownloadLink from '@/helpers/profile/buildDownloadLink'

import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import DataViewSortHeader from '@nicolaudie-group/ng-ui-kit/components/DataView/DataViewSortHeader.vue'
import TableCell from '@nicolaudie-group/ng-ui-kit/components/Table/TableCell.vue'
import TableRow from '@nicolaudie-group/ng-ui-kit/components/Table/TableRow.vue'
import UiTable from '@nicolaudie-group/ng-ui-kit/components/Table/UiTable.vue'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import NoItemFound from '@nicolaudie-group/ng-ui-kit/components/NoItemFound/NoItemFound.vue'
import DataViewFooter from '@nicolaudie-group/ng-ui-kit/components/DataView/DataViewFooter.vue'
import DataViewHeader from '@nicolaudie-group/ng-ui-kit/components/DataView/DataViewHeader.vue'
import UiBadge from '@nicolaudie-group/ng-ui-kit/components/UiBadge/UiBadge.vue'
import UiTypo from '@nicolaudie-group/ng-ui-kit/components/UiTypo/UiTypo.vue'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import useStatus from '@nicolaudie-group/ng-ui-kit/composables/useStatus'
import { SIZE_SMALL } from '@nicolaudie-group/ng-ui-kit/components/Listbox/symbols'
import * as prod from '@nicolaudie-group/ng-ui-kit/helpers/prod'
import useAction from '@/composables/useAction'
import usePaginationParams from '@/composables/DataView/usePaginationParams'
import { checkChanged } from '@/composables/DataView/helpers'
import useSortParams from '@/composables/DataView/useSortParams'
import useTranslate from '@/composables/useTranslate'
import formatDate from '@/helpers/formatDate'
import {
  buildTicketLink,
  buildDocumentationLink,
  buildOrderLink,
  buildLegacyDownloadLink,
} from '@/helpers/profileRequests'
import SelectField from '../SelectField/SelectField.vue'

const stringifyId = (id) => (id ? String(id) : undefined)

const states = {
  PENDING: 'warning',
  ASSIGNED: 'info',
  PROCESSING: 'info',
  PROBLEM: 'error',
  WAITING_INFO: 'error',
  FINISHED: 'success',
}

function useRequestsQuery() {
  const { translate } = useTranslate()

  const { action: get, data: response, status } = useAction(
    async ({ page, limitBy, ...query }) => {
      const result = await sdk.v3.profileRequests.getMany({
        query: {
          nPage: page,
          nLimit: limitBy,
          ...query,
        },
      })

      return markRaw(result)
    },
    {
      /*
      We need the full SDK Result object for pagination, but Vue can't make it
      reactive (see this issue https://github.com/vuejs/core/issues/3024).
      As a workaround, we use a shallowRef so that it's not making it deeply reactive.
      Which is better anyways, since we (should) never mutate the result anyways,
      saving us a little bit of resources here.
      */
      shallow: true,
    },
  )

  const requests = computed(() => (
    (response.value?.data || []).map((item) => {
      const profileId = stringifyId(item.SSL_ID) ?? stringifyId(item.SRQ_PREVIOUS_SSL_ID)
      const profileUid = item.SRQ_SSL_UID

      let profileDownloadLink = null

      if (profileUid) {
        profileDownloadLink = buildDownloadLink(profileUid, 'ssl2')
      } else if (profileId) {
        profileDownloadLink = buildLegacyDownloadLink(profileId)
      }

      try {
        const result = {
          ...item,
          profileUid,
          formattedStatus: translate(`pb2.requestsList.status.${item.SRQ_STATE}`),
          statusStyle: states[item.SRQ_STATE],
          formattedRequestDate: formatDate(item.SRQ_DATE_REQUEST),
          ticketLink: buildTicketLink(item.SRQ_TICKET_STI_ID),
          documentationLink: buildDocumentationLink(item.SRQ_FILENAME),
          orderLink: item.SRQ_ORDER ? buildOrderLink(item.SRQ_ORDER) : null,
          orderPaid: item.SRQ_ORDER ? item.ORD_PAID === 'PAID' : null,
          profileLink: profileUid ? { name: routeNames.Properties, params: { profileUid } } : null,
          profileDownloadLink,
        }
        dev.log(result)
        return result
      } catch (e) {
        prod.error(e)
        return null
      }
    })
  ))

  const count = computed(() => (
    response.value?.pagination.count || 0
  ))

  return {
    get,
    requests,
    count,
    status,
  }
}

const SORTBY = {
  UID: 'SRQ_ID',
  CUSTOMER_UID: 'SRQ_CUSTOMER_CTC_ID',
  BRAND: 'SRQ_SSL_BRAND',
  NAME: 'SRQ_SSL_NAME',
  DATE_REQUEST: 'SRQ_DATE_REQUEST',
  STATE: 'SRQ_STATE',
  TEXT_REQUEST: 'SRQ_TEXT_REQUEST',
  FILENAME: 'SRQ_FILENAME',
  ORDER_UID: 'SRQ_ORDER',
  PRIORITY: 'SRQ_PRIORITY',
  SOLUTION: 'SRQ_SOLUTION',
  REQUEST_TYPE: 'SRQ_REQUEST_TYPE',
  PROBLEM: 'SRQ_PROBLEM',
  ASSIGN_DATE: 'SRQ_ASSIGN_DATE',
  ASSIGN_TO: 'SRQ_ASSIGN_TO',
  ASSIGN_BY: 'SRQ_ASSIGN_BY',
  PROBLEM_DATE: 'SRQ_PROBLEM_DATE',
  LANGUAGE: 'SRQ_LANGUAGE',
  UPDATE_DATE: 'SRQ_UPDATE_DATE',
  DUE_DATE: 'SRQ_DUE_DATE',
  START_DATE: 'SRQ_SSL_START_DATE',
  FINISH_DATE: 'SRQ_FINISH_DATE',
  IGNORED: 'SRQ_IGNORED',
  PROFILE_UID: 'SRQ_PREVIOUS_SSL_ID',
  TICKET_UID: 'SRQ_TICKET_STI_ID',
  PRIORITY_FORCED: 'SRQ_PRIORITY_FORCED',
}
const ALL_SORTBY = Object.values(SORTBY)
const DEFAULT_SORTBY = SORTBY.DATE_REQUEST

function useRequestsList() {
  const {
    count,
    get,
    requests,
    status,
  } = useRequestsQuery()

  const { pending: loading, success: loaded } = useStatus(status)

  const pagination = usePaginationParams(count, loaded, { query: false, loading, defaultLimit: 5 })

  const sort = useSortParams({
    query: false, loading, ALL_SORTBY, DEFAULT_SORTBY,
  })

  const requestParams = computed((previous = {}) => {
    const result = {
      ...previous,
      page: pagination.page.value,
      limitBy: pagination.limit.value,
      sortBy: sort.sortBy.value,
      sortOrder: sort.sortOrder.value,
      state: undefined, // TODO: implement search/filtering
      brand: undefined, // TODO: implement search/filtering
      name: undefined, // TODO: implement search/filtering
    }

    return checkChanged(result, previous)
  })

  /**
   * Number of placeholders to display while fetching data
   */
  const placeholderCount = computed(() => (
    pagination.itemsInPage.value > 0 ? pagination.itemsInPage.value : 10
  ))

  async function load() {
    dev.info('Loading requests', requestParams.value)
    await get(requestParams.value)
  }

  const context = {
    pagination,
    sort,
    requestParams,
    load,
    loading,
    loaded,
    requests,
    placeholderCount,
  }

  return context
}
</script>

<script setup>
const { translate } = useTranslate()

const {
  load,
  loading,
  pagination,
  requestParams,
  sort,
  requests,
  placeholderCount,
} = useRequestsList()

const {
  paginationProps,
  paginationText,
} = pagination

const {
  sortProps,
} = sort

watch(requestParams, load, {
  // Load the requests on initialization
  immediate: true,
  /*
  Wait for the component to be ready (rendered, or in this case what we want is unmounted)
  before loading the requests, so that it's not triggered needlessly when
  navigating away because the queries changes to undefined
  */
  flush: 'post',
})
</script>
